<template>
  <v-container fluid class="pa-6">
    <!-- Header Section -->
    <v-row class="mb-4">
      <v-col cols="12">
        <div class="d-flex align-center justify-space-between flex-wrap">
          <div>
            <h1 class="dashboard-title">Dashboard de Tienda</h1>
            <p class="text-caption grey--text">
              Resumen de operaciones del día • {{ currentDate }}
            </p>
          </div>
          <v-btn
            color="primary"
            @click="get_report"
            :loading="loading_status"
            class="mt-2 mt-sm-0"
          >
            <v-icon left>mdi-refresh</v-icon>
            Actualizar
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- KPI Cards Section -->
    <v-row class="mb-4 mb-md-6">
      <v-col cols="12" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex success-gradient">
              <v-icon color="white">mdi-cash-register</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Ventas del Día</div>
              <div class="kpi-value">{{ formatCurrency(totalSales) }}</div>
              <div class="kpi-trend">
                <v-icon x-small :color="salesTrend.color">{{ salesTrend.icon }}</v-icon>
                <span :class="`${salesTrend.color}--text`">{{ salesTrend.text }}</span>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex primary-gradient">
              <v-icon color="white">mdi-package-variant-closed</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Valor Inventario</div>
              <div class="kpi-value">{{ formatCurrency(totalInventoryValue) }}</div>
              <div class="kpi-subtitle d-none d-sm-flex">
                {{ totalInventoryItems }} productos
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex warning-gradient">
              <v-icon color="white">mdi-percent</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Margen Bruto</div>
              <div class="kpi-value">{{ grossMargin }}%</div>
              <div class="kpi-subtitle d-none d-sm-flex">
                Utilidad: {{ formatCurrency(grossProfit) }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex info-gradient">
              <v-icon color="white">mdi-chart-line</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Movimientos</div>
              <div class="kpi-value">{{ totalMovements }}</div>
              <div class="kpi-subtitle d-none d-sm-flex">
                <span class="success--text">↑{{ totalIn }}</span>
                <span class="mx-1">|</span>
                <span class="error--text">↓{{ totalOut }}</span>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Charts Section with Sparklines -->
    <v-row class="mb-4">
      <!-- Sales Distribution -->
      <v-col cols="12" lg="6">
        <v-card class="chart-card elevation-0" outlined>
          <v-card-title class="pa-4 pb-2">
            <v-icon color="success" class="mr-2">mdi-chart-line</v-icon>
            Tendencia de Ventas por Categoría
          </v-card-title>
          <v-card-text class="pa-4 pt-0">
            <v-row v-for="(item, index) in salesChartData" :key="index" class="mb-3">
              <v-col cols="12">
                <div class="d-flex justify-space-between align-center mb-1">
                  <span class="text-body-2 font-weight-medium">{{ item.category }}</span>
                  <span class="text-caption grey--text">
                    {{ formatNumber(item.value) }} und ({{ item.percentage }}%)
                  </span>
                </div>
                <v-sparkline
                  :value="item.trend"
                  :gradient="['#43A047', '#66BB6A', '#81C784']"
                  :line-width="2"
                  :padding="8"
                  :smooth="10"
                  :height="40"
                  :auto-draw="true"
                  :show-labels="false"
                  :label-size="3"
                  type="trend"
                >
                  <template v-slot:label="item">
                    {{ item.value }}
                  </template>
                </v-sparkline>
                <v-progress-linear
                  :value="item.percentage"
                  :color="getProgressColor(index)"
                  height="6"
                  rounded
                  class="mt-1"
                ></v-progress-linear>
              </v-col>
            </v-row>

            <!-- Mini summary -->
            <v-divider class="my-3"></v-divider>
            <div class="d-flex justify-space-between align-center">
              <span class="text-caption grey--text">Total categorías activas</span>
              <v-chip x-small label color="success" outlined>
                {{ salesChartData.length }}
              </v-chip>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Inventory Composition -->
      <v-col cols="12" lg="6">
        <v-card class="chart-card elevation-0" outlined>
          <v-card-title class="pa-4 pb-2">
            <v-icon color="primary" class="mr-2">mdi-chart-bar</v-icon>
            Composición del Inventario
          </v-card-title>
          <v-card-text class="pa-4 pt-0">
            <v-row v-for="(item, index) in inventoryChartData" :key="index" class="mb-3">
              <v-col cols="12">
                <div class="d-flex justify-space-between align-center mb-1">
                  <span class="text-body-2 font-weight-medium">{{ item.category }}</span>
                  <span class="text-caption grey--text">
                    {{ formatCurrency(item.value) }} ({{ item.percentage }}%)
                  </span>
                </div>
                <v-sparkline
                  :value="item.distribution"
                  :gradient="['#1976D2', '#42A5F5', '#64B5F6']"
                  :line-width="2"
                  :padding="8"
                  :smooth="10"
                  :height="40"
                  :auto-draw="true"
                  :fill="true"
                  type="bar"
                >
                </v-sparkline>
                <v-progress-linear
                  :value="item.percentage"
                  :color="getProgressColor(index + 3)"
                  height="6"
                  rounded
                  class="mt-1"
                ></v-progress-linear>
              </v-col>
            </v-row>

            <!-- Mini summary -->
            <v-divider class="my-3"></v-divider>
            <div class="d-flex justify-space-between align-center">
              <span class="text-caption grey--text">Valor total inventario</span>
              <v-chip x-small label color="primary" outlined>
                {{ formatCurrency(totalInventoryValue) }}
              </v-chip>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Data Tables Section -->
    <v-row>
      <!-- Sales Summary -->
      <v-col cols="12" lg="6">
        <v-card class="data-card elevation-0" outlined>
          <v-card-title class="pa-0">
            <v-toolbar flat class="minimal-toolbar">
              <v-icon color="success" class="mr-2">mdi-cash-multiple</v-icon>
              <h3 class="card-title">Ventas por Categoría</h3>
              <v-spacer></v-spacer>
              <v-chip small label color="success" text-color="white">
                {{ sales.length }} categorías
              </v-chip>
            </v-toolbar>
          </v-card-title>

          <v-data-table
            :headers="header_sales"
            :items="sales"
            :items-per-page="5"
            hide-default-footer
            class="minimal-table"
            :loading="loading_status"
            dense
          >
            <template v-slot:[`item.category`]="{ item }">
              <div class="category-cell">
                <v-avatar
                  size="8"
                  :color="getCategoryColor(item.category)"
                  class="mr-2"
                ></v-avatar>
                {{ item.category }}
              </div>
            </template>

            <template v-slot:[`item.quantity`]="{ item }">
              <v-chip x-small label>{{ formatNumber(item.quantity) }}</v-chip>
            </template>

            <template v-slot:[`item.discount`]="{ item }">
              <span class="red--text">{{ formatCurrency(item.discount) }}</span>
            </template>

            <template v-slot:[`item.price`]="{ item }">
              <span class="font-weight-medium">{{ formatCurrency(item.price) }}</span>
            </template>

            <template v-slot:body.append>
              <tr class="grey lighten-4 font-weight-bold">
                <td>TOTAL</td>
                <td class="text-center">
                  {{ formatNumber(sumTableNumber("sales", "quantity")) }}
                </td>
                <td class="text-right red--text">
                  {{ formatCurrency(sumTableNumber("sales", "discount")) }}
                </td>
                <td class="text-right">
                  {{ formatCurrency(sumTableNumber("sales", "price")) }}
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-col>

      <!-- Inventory Summary -->
      <v-col cols="12" lg="6">
        <v-card class="data-card elevation-0" outlined>
          <v-card-title class="pa-0">
            <v-toolbar flat class="minimal-toolbar">
              <v-icon color="primary" class="mr-2">mdi-package-variant</v-icon>
              <h3 class="card-title">Inventario Actual</h3>
              <v-spacer></v-spacer>
              <v-chip small label color="primary" text-color="white">
                {{ inventory.length }} categorías
              </v-chip>
            </v-toolbar>
          </v-card-title>

          <v-data-table
            :headers="header_inventory"
            :items="inventory"
            :items-per-page="5"
            hide-default-footer
            class="minimal-table"
            :loading="loading_status"
            dense
          >
            <template v-slot:[`item.categoria`]="{ item }">
              <div class="category-cell">
                <v-avatar
                  size="8"
                  :color="getCategoryColor(item.categoria)"
                  class="mr-2"
                ></v-avatar>
                {{ item.categoria }}
              </div>
            </template>

            <template v-slot:[`item.quantity`]="{ item }">
              <v-chip x-small label color="primary" outlined>
                {{ formatNumber(item.quantity) }}
              </v-chip>
            </template>

            <template v-slot:[`item.price`]="{ item }">
              <span class="font-weight-medium">{{ formatCurrency(item.price) }}</span>
            </template>

            <template v-slot:body.append>
              <tr class="grey lighten-4 font-weight-bold">
                <td>TOTAL</td>
                <td class="text-center">
                  {{ formatNumber(sumTableNumber("inventory", "quantity")) }}
                </td>
                <td class="text-right">
                  {{ formatCurrency(sumTableNumber("inventory", "price")) }}
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-col>

      <!-- Payment Methods -->
      <v-col cols="12" lg="6">
        <v-card class="data-card elevation-0" outlined>
          <v-card-title class="pa-0">
            <v-toolbar flat class="minimal-toolbar">
              <v-icon color="info" class="mr-2">mdi-credit-card</v-icon>
              <h3 class="card-title">Formas de Pago</h3>
            </v-toolbar>
          </v-card-title>

          <v-data-table
            :headers="header_payments"
            :items="payments"
            :items-per-page="5"
            hide-default-footer
            class="minimal-table"
            :loading="loading_status"
            dense
          >
            <template v-slot:[`item.type`]="{ item }">
              <div class="payment-method">
                <v-icon small class="mr-2" :color="getPaymentColor(item.type)">
                  {{ getPaymentIcon(item.type) }}
                </v-icon>
                {{ item.type }}
              </div>
            </template>

            <template v-slot:[`item.quantity`]="{ item }">
              <span class="font-weight-medium">{{ formatNumber(item.quantity) }}</span>
            </template>

            <template v-slot:[`item.value`]="{ item }">
              <div class="text-right">
                <div class="font-weight-medium">{{ formatCurrency(item.value) }}</div>
                <div class="text-caption grey--text">
                  {{ getPercentage(item.value, sumTableNumber("payments", "value")) }}%
                </div>
              </div>
            </template>

            <template v-slot:body.append>
              <tr class="grey lighten-4 font-weight-bold">
                <td>TOTAL</td>
                <td class="text-center">
                  {{ formatNumber(sumTableNumber("payments", "quantity")) }}
                </td>
                <td class="text-right">
                  {{ formatCurrency(sumTableNumber("payments", "value")) }}
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-col>

      <!-- Events Summary -->
      <v-col cols="12" lg="6">
        <v-card class="data-card elevation-0" outlined>
          <v-card-title class="pa-0">
            <v-toolbar flat class="minimal-toolbar">
              <v-icon color="warning" class="mr-2">mdi-calendar-check</v-icon>
              <h3 class="card-title">Eventos del Día</h3>
            </v-toolbar>
          </v-card-title>

          <v-data-table
            :headers="header_events"
            :items="events"
            :items-per-page="5"
            hide-default-footer
            class="minimal-table"
            :loading="loading_status"
            dense
          >
            <template v-slot:[`item.concept`]="{ item }">
              <v-chip small :color="getEventColor(item.concept)" dark label>
                {{ item.concept }}
              </v-chip>
            </template>

            <template v-slot:[`item.quantity`]="{ item }">
              <span class="font-weight-medium">{{ formatNumber(item.quantity) }}</span>
            </template>

            <template v-slot:[`item.price`]="{ item }">
              <span class="font-weight-medium">{{ formatCurrency(item.price) }}</span>
            </template>

            <template v-slot:body.append>
              <tr class="grey lighten-4 font-weight-bold">
                <td>TOTAL</td>
                <td class="text-center">
                  {{ formatNumber(sumTableNumber("events", "quantity")) }}
                </td>
                <td class="text-right">
                  {{ formatCurrency(sumTableNumber("events", "price")) }}
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-col>

      <!-- Cash Flow -->
      <v-col cols="12">
        <v-row>
          <!-- Cash In -->
          <v-col cols="12" md="6">
            <v-card class="data-card elevation-0 success-border" outlined>
              <v-card-title class="pa-0">
                <v-toolbar flat class="minimal-toolbar">
                  <v-icon color="success" class="mr-2">mdi-arrow-down-bold</v-icon>
                  <h3 class="card-title">Ingresos</h3>
                  <v-spacer></v-spacer>
                  <span class="success--text font-weight-bold">
                    +{{ formatCurrency(sumTableNumber("cin", "doc_val")) }}
                  </span>
                </v-toolbar>
              </v-card-title>

              <v-data-table
                :headers="header_cash_flow"
                :items="cin"
                :items-per-page="5"
                hide-default-footer
                class="minimal-table"
                :loading="loading_status"
                dense
              >
                <template v-slot:[`item.concept`]="{ item }">
                  {{ item.concept }}
                </template>

                <template v-slot:[`item.quantity`]="{ item }">
                  <v-chip x-small label color="success" outlined>
                    {{ formatNumber(item.quantity) }}
                  </v-chip>
                </template>

                <template v-slot:[`item.doc_val`]="{ item }">
                  <span class="success--text font-weight-medium">
                    +{{ formatCurrency(item.doc_val) }}
                  </span>
                </template>
              </v-data-table>
            </v-card>
          </v-col>

          <!-- Cash Out -->
          <v-col cols="12" md="6">
            <v-card class="data-card elevation-0 error-border" outlined>
              <v-card-title class="pa-0">
                <v-toolbar flat class="minimal-toolbar">
                  <v-icon color="error" class="mr-2">mdi-arrow-up-bold</v-icon>
                  <h3 class="card-title">Egresos</h3>
                  <v-spacer></v-spacer>
                  <span class="error--text font-weight-bold">
                    -{{ formatCurrency(sumTableNumber("cout", "doc_val")) }}
                  </span>
                </v-toolbar>
              </v-card-title>

              <v-data-table
                :headers="header_cash_flow"
                :items="cout"
                :items-per-page="5"
                hide-default-footer
                class="minimal-table"
                :loading="loading_status"
                dense
              >
                <template v-slot:[`item.concept`]="{ item }">
                  {{ item.concept }}
                </template>

                <template v-slot:[`item.quantity`]="{ item }">
                  <v-chip x-small label color="error" outlined>
                    {{ formatNumber(item.quantity) }}
                  </v-chip>
                </template>

                <template v-slot:[`item.doc_val`]="{ item }">
                  <span class="error--text font-weight-medium">
                    -{{ formatCurrency(item.doc_val) }}
                  </span>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>
      </v-col>

      <!-- Financial Summary -->
      <v-col cols="12">
        <v-card class="summary-card elevation-0" outlined>
          <v-card-title class="pa-0">
            <v-toolbar flat class="minimal-toolbar" color="primary" dark>
              <v-icon class="mr-2">mdi-calculator</v-icon>
              <h3 class="card-title">Resumen Financiero</h3>
            </v-toolbar>
          </v-card-title>

          <v-card-text class="pa-0">
            <v-simple-table class="summary-table">
              <tbody>
                <tr v-for="item in financialSummary" :key="item.label">
                  <td class="text-left pa-4">
                    <div class="d-flex align-center">
                      <v-icon small class="mr-2" :color="item.color">{{
                        item.icon
                      }}</v-icon>
                      <span class="font-weight-medium">{{ item.label }}</span>
                    </div>
                  </td>
                  <td class="text-right pa-4">
                    <span class="text-h6 font-weight-bold" :class="`${item.color}--text`">
                      {{ formatCurrency(item.value) }}
                    </span>
                  </td>
                </tr>
                <tr class="grey lighten-3">
                  <td class="text-left pa-4">
                    <div class="d-flex align-center">
                      <v-icon class="mr-2">mdi-sigma</v-icon>
                      <span class="text-h6 font-weight-bold">Balance del Día</span>
                    </div>
                  </td>
                  <td class="text-right pa-4">
                    <span
                      class="text-h5 font-weight-bold"
                      :class="netBalance >= 0 ? 'success--text' : 'error--text'"
                    >
                      {{ formatCurrency(netBalance) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Loading Dialog -->
    <v-dialog v-model="loading_status" persistent max-width="400">
      <v-card>
        <v-card-text class="pa-6">
          <div class="text-center">
            <v-progress-circular
              indeterminate
              color="primary"
              size="64"
              class="mb-4"
            ></v-progress-circular>
            <h3 class="text-h6 mb-2">Procesando Información</h3>
            <p class="text-body-2 grey--text">{{ lStatus }}</p>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
      right
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false"> Cerrar </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { webserver, getToday } from "../../services/webserver.js";

export default {
  name: "StoreResume",
  components: {},

  data() {
    return {
      // Loading states
      loading_status: false,
      lStatus: "",

      // Data
      sales: [],
      inventory: [],
      events: [],
      payments: [],
      cin: [],
      cout: [],
      resume: [
        { label: "Ventas totales", value: 0 },
        { label: "Descuentos", value: 0 },
        { label: "Cambios", value: 0 },
        { label: "Garantías", value: 0 },
      ],

      // Totals
      totalSales: 0,
      totalDiscounts: 0,
      totalCambio: 0,
      totalGarantia: 0,

      // Settings
      settings: null,
      currentDate: new Date().toLocaleDateString("es-CO", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      }),

      // Snackbar
      snackbar: {
        show: false,
        text: "",
        color: "info",
        timeout: 3000,
      },

      // Sparkline data
      salesChartData: [],
      inventoryChartData: [],

      // Table Headers
      header_sales: [
        {
          text: "Categoría",
          value: "category",
          sortable: false,
          width: "40%",
        },
        {
          text: "Cant.",
          value: "quantity",
          align: "center",
          width: "20%",
        },
        {
          text: "Desc.",
          value: "discount",
          align: "right",
          width: "20%",
        },
        {
          text: "Valor",
          value: "price",
          align: "right",
          width: "20%",
        },
      ],

      header_inventory: [
        {
          text: "Categoría",
          value: "categoria",
          sortable: false,
          width: "50%",
        },
        {
          text: "Cantidad",
          value: "quantity",
          align: "center",
          width: "25%",
        },
        {
          text: "Valor",
          value: "price",
          align: "right",
          width: "25%",
        },
      ],

      header_events: [
        {
          text: "Evento",
          value: "concept",
          sortable: false,
          width: "50%",
        },
        {
          text: "Cant.",
          value: "quantity",
          align: "center",
          width: "25%",
        },
        {
          text: "Valor",
          value: "price",
          align: "right",
          width: "25%",
        },
      ],

      header_payments: [
        {
          text: "Método",
          value: "type",
          sortable: false,
          width: "40%",
        },
        {
          text: "Trans.",
          value: "quantity",
          align: "center",
          width: "20%",
        },
        {
          text: "Valor",
          value: "value",
          align: "right",
          width: "40%",
        },
      ],

      header_cash_flow: [
        {
          text: "Concepto",
          value: "concept",
          sortable: false,
          width: "50%",
        },
        {
          text: "Cant.",
          value: "quantity",
          align: "center",
          width: "25%",
        },
        {
          text: "Valor",
          value: "doc_val",
          align: "right",
          width: "25%",
        },
      ],
    };
  },

  computed: {
    // KPI Calculations
    totalInventoryValue() {
      return this.sumTableNumber("inventory", "price");
    },

    totalInventoryItems() {
      return this.sumTableNumber("inventory", "quantity");
    },

    grossMargin() {
      if (this.totalSales === 0) return 0;
      const profit = this.totalSales - this.totalDiscounts;
      return Math.round((profit / this.totalSales) * 100);
    },

    grossProfit() {
      return this.totalSales - this.totalDiscounts;
    },

    totalMovements() {
      return this.sumTableNumber("events", "quantity");
    },

    totalIn() {
      return this.sumTableNumber("cin", "quantity");
    },

    totalOut() {
      return this.sumTableNumber("cout", "quantity");
    },

    salesTrend() {
      // This would compare with previous period in real implementation
      return {
        icon: "mdi-trending-up",
        color: "success",
        text: "+15.3% vs ayer",
      };
    },

    financialSummary() {
      return [
        {
          label: "Ventas Brutas",
          value: this.totalSales,
          icon: "mdi-cash-plus",
          color: "success",
        },
        {
          label: "Descuentos Aplicados",
          value: -this.totalDiscounts,
          icon: "mdi-sale",
          color: "warning",
        },
        {
          label: "Devoluciones y Cambios",
          value: -(this.totalCambio + this.totalGarantia),
          icon: "mdi-undo",
          color: "error",
        },
        {
          label: "Otros Ingresos",
          value: this.sumTableNumber("cin", "doc_val"),
          icon: "mdi-plus-circle",
          color: "info",
        },
        {
          label: "Otros Egresos",
          value: -this.sumTableNumber("cout", "doc_val"),
          icon: "mdi-minus-circle",
          color: "deep-orange",
        },
      ];
    },

    netBalance() {
      return this.financialSummary.reduce((sum, item) => sum + item.value, 0);
    },
  },

  mounted() {
    this.settings = window.settings;
    this.get_report();
  },

  methods: {
    // Formatting methods
    formatCurrency(value) {
      if (!value && value !== 0) return "$0";
      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    },

    formatNumber(value) {
      if (!value && value !== 0) return "0";
      return parseFloat(value).toLocaleString("es-CO");
    },

    // Utility methods
    sumTable(table, key) {
      return this.sumTableNumber(table, key).toLocaleString("es-CO", {
        minimumFractionDigits: 2,
      });
    },

    sumTableNumber(table, key) {
      return this[table].reduce((sum, item) => sum + parseFloat(item[key] || 0), 0);
    },

    getPercentage(value, total) {
      if (!total || total === 0) return 0;
      return Math.round((value / total) * 100);
    },

    getCategoryColor(category) {
      const colors = [
        "#1976D2",
        "#388E3C",
        "#F57C00",
        "#D32F2F",
        "#7B1FA2",
        "#00796B",
        "#5D4037",
        "#455A64",
      ];
      const index = Math.abs(category.charCodeAt(0) - 65) % colors.length;
      return colors[index];
    },

    getPaymentIcon(payment) {
      const icons = {
        EFECTIVO: "mdi-cash",
        TARJETA: "mdi-credit-card",
        TRANSFERENCIA: "mdi-bank-transfer",
        CREDITO: "mdi-calendar-clock",
        CAMBIO: "mdi-swap-horizontal",
        GARANTIA: "mdi-shield-check",
      };
      const key = Object.keys(icons).find((k) => payment.toUpperCase().includes(k));
      return icons[key] || "mdi-cash-multiple";
    },

    getPaymentColor(payment) {
      const colors = {
        EFECTIVO: "success",
        TARJETA: "primary",
        TRANSFERENCIA: "info",
        CREDITO: "warning",
        CAMBIO: "orange",
        GARANTIA: "deep-purple",
      };
      const key = Object.keys(colors).find((k) => payment.toUpperCase().includes(k));
      return colors[key] || "grey";
    },

    getEventColor(event) {
      const colors = {
        VENTA: "success",
        COMPRA: "primary",
        DEVOLUCION: "warning",
        AJUSTE: "info",
        TRASLADO: "purple",
      };
      const key = Object.keys(colors).find((k) => event.toUpperCase().includes(k));
      return colors[key] || "grey";
    },

    // New method for progress colors
    getProgressColor(index) {
      const colors = [
        "success",
        "primary",
        "warning",
        "error",
        "purple",
        "teal",
        "orange",
        "indigo",
      ];
      return colors[index % colors.length];
    },

    // Generate fake trend data for sparklines
    generateTrendData(baseValue, points = 7) {
      const trend = [];
      let current = baseValue;
      for (let i = 0; i < points; i++) {
        current = current + (Math.random() - 0.5) * (baseValue * 0.3);
        trend.push(Math.max(0, Math.round(current)));
      }
      return trend;
    },

    // Generate distribution data for sparklines
    generateDistributionData(value, total, points = 5) {
      const distribution = [];
      const percentage = (value / total) * 100;
      for (let i = 0; i < points; i++) {
        distribution.push(Math.round(percentage + (Math.random() - 0.5) * 10));
      }
      return distribution;
    },

    // Message handling
    showMessage(text, color = "info", timeout = 3000) {
      this.snackbar = {
        show: true,
        text,
        color,
        timeout,
      };
    },

    // Data loading
    async get_report() {
      this.loading_status = true;
      this.totalSales = 0;
      this.totalDiscounts = 0;
      this.totalCambio = 0;
      this.totalGarantia = 0;

      const reports = ["sales", "inventory", "payments", "events", "cashIN", "cashOUT"];

      try {
        for (const report of reports) {
          const qry = {
            store: window.store.store_id,
            issueDate: getToday(),
            report: report,
          };

          this.lStatus = `Consultando ${report}...`;

          const data = await new Promise((resolve, reject) => {
            webserver("cash_register_report_b", qry, resolve, reject);
          });

          this.processReportData(report, data || []);
        }

        this.loading_status = false;
        this.showMessage("Dashboard actualizado correctamente", "success");
      } catch (error) {
        this.loading_status = false;
        this.showMessage("Error al cargar los datos", "error");
        console.error(error);
      }
    },

    processReportData(report, data) {
      switch (report) {
        case "sales": {
          data.forEach((item) => {
            if (this.settings?.CATEGORIA) {
              const cat = this.settings.CATEGORIA.find(
                (itm) => itm.codigo === item.categoria
              );
              item.category = cat ? cat.valor : item.categoria;
            } else {
              item.category = item.categoria;
            }
            item.quantity = parseFloat(item.quantity || 0);
            item.price = parseFloat(item.price || 0);
            item.discount = parseFloat(item.discount || 0);

            this.totalSales += item.price;
            this.totalDiscounts += item.discount;
          });

          this.sales = data;
          this.resume[0].value = this.totalSales;
          this.resume[1].value = this.totalDiscounts;

          // Process chart data for sparklines
          const salesByCategory = {};
          let totalQty = 0;

          data.forEach((item) => {
            if (!salesByCategory[item.category]) {
              salesByCategory[item.category] = 0;
            }
            salesByCategory[item.category] += item.quantity;
            totalQty += item.quantity;
          });

          // Convert to sparkline format
          this.salesChartData = Object.entries(salesByCategory)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5) // Top 5 categories
            .map(([category, value]) => ({
              category,
              value,
              percentage: totalQty > 0 ? Math.round((value / totalQty) * 100) : 0,
              trend: this.generateTrendData(value),
            }));

          break;
        }

        case "inventory": {
          data.forEach((item) => {
            item.quantity = parseFloat(item.quantity || 0);
            item.price = parseFloat(item.price || 0);
          });

          this.inventory = data;

          // Process chart data for sparklines
          const inventoryByCategory = {};
          let totalValue = 0;

          data.forEach((item) => {
            if (!inventoryByCategory[item.categoria]) {
              inventoryByCategory[item.categoria] = { quantity: 0, value: 0 };
            }
            inventoryByCategory[item.categoria].quantity += item.quantity;
            inventoryByCategory[item.categoria].value += item.price;
            totalValue += item.price;
          });

          // Convert to sparkline format
          this.inventoryChartData = Object.entries(inventoryByCategory)
            .sort((a, b) => b[1].value - a[1].value)
            .slice(0, 5) // Top 5 categories by value
            .map(([category, data]) => ({
              category,
              value: data.value,
              quantity: data.quantity,
              percentage:
                totalValue > 0 ? Math.round((data.value / totalValue) * 100) : 0,
              distribution: this.generateDistributionData(data.value, totalValue),
            }));

          break;
        }

        case "payments":
          data.forEach((item) => {
            item.quantity = parseFloat(item.quantity || 0);
            item.value = parseFloat(item.value || 0);

            if (item.type === "CAMBIO") {
              this.totalCambio += item.value;
              this.resume[2].value = this.totalCambio;
            }
            if (item.type === "GARANTIA") {
              this.totalGarantia += item.value;
              this.resume[3].value = this.totalGarantia;
            }
          });

          this.payments = data;
          break;

        case "events":
          data.forEach((item) => {
            item.quantity = parseFloat(item.quantity || 0);
            item.price = parseFloat(item.price || 0);
          });
          this.events = data;
          break;

        case "cashIN":
          data.forEach((item) => {
            item.quantity = parseFloat(item.quantity || 0);
            item.doc_val = parseFloat(item.doc_val || 0);
          });
          this.cin = data;
          break;

        case "cashOUT":
          data.forEach((item) => {
            item.quantity = parseFloat(item.quantity || 0);
            item.doc_val = parseFloat(item.doc_val || 0);
          });
          this.cout = data;
          break;
      }
    },
  },
};
</script>

<style scoped>
/* Dashboard Title */
.dashboard-title {
  font-size: 2rem;
  font-weight: 300;
  color: #333;
}

/* KPI Cards */
.kpi-card {
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.kpi-card .v-card__text {
  display: flex;
  align-items: center;
  gap: 16px;
}

.kpi-icon {
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kpi-icon .v-icon {
  font-size: 32px !important;
}

.kpi-content {
  flex: 1;
  min-width: 0;
}

.kpi-label {
  font-size: 0.875rem;
  color: #666;
  font-weight: 500;
  margin-bottom: 4px;
}

.kpi-value {
  font-size: 1.875rem;
  font-weight: 700;
  color: #333;
  line-height: 1.2;
}

.kpi-subtitle {
  font-size: 0.75rem;
  color: #999;
  margin-top: 4px;
}

.kpi-trend {
  font-size: 0.75rem;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Gradients */
.success-gradient {
  background: linear-gradient(135deg, #43a047 0%, #66bb6a 100%);
}

.primary-gradient {
  background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
}

.warning-gradient {
  background: linear-gradient(135deg, #fb8c00 0%, #ffa726 100%);
}

.info-gradient {
  background: linear-gradient(135deg, #00acc1 0%, #26c6da 100%);
}

/* Cards */
.chart-card,
.data-card,
.summary-card {
  border: 1px solid #e0e0e0;
  height: 100%;
}

.success-border {
  border-left: 4px solid #4caf50 !important;
}

.error-border {
  border-left: 4px solid #f44336 !important;
}

/* Tables */
.minimal-toolbar {
  border-bottom: 1px solid #e0e0e0;
  padding: 16px 24px;
}

.card-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #333;
}

.minimal-table {
  border: none !important;
}

.minimal-table >>> .v-data-table__wrapper {
  border: none;
}

.minimal-table >>> thead tr th {
  background-color: #fafafa !important;
  font-weight: 600 !important;
  color: #666 !important;
  border-bottom: 2px solid #e0e0e0 !important;
  font-size: 0.875rem !important;
  padding: 12px 16px !important;
}

.minimal-table >>> tbody tr {
  border-bottom: 1px solid #f0f0f0 !important;
}

.minimal-table >>> tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.minimal-table >>> tbody tr td {
  padding: 12px 16px !important;
  font-size: 0.875rem !important;
}

/* Custom cells */
.category-cell {
  display: flex;
  align-items: center;
}

.payment-method {
  display: flex;
  align-items: center;
}

/* Summary table */
.summary-table tbody tr:hover {
  background-color: #f5f5f5;
}

/* Loading */
.minimal-table >>> .v-data-table__progress {
  height: 2px !important;
}

/* Responsive */
@media (max-width: 600px) {
  .dashboard-title {
    font-size: 1.5rem;
  }

  .kpi-value {
    font-size: 1.5rem;
  }

  .kpi-card .v-card__text {
    flex-direction: column;
    text-align: center;
    padding: 16px !important;
  }

  .kpi-icon {
    margin-bottom: 8px;
  }

  .minimal-toolbar {
    padding: 12px 16px !important;
  }
}

@media (max-width: 960px) {
  .kpi-value {
    font-size: 1.625rem;
  }

  .kpi-card .v-card__text {
    flex-direction: row;
    text-align: left;
  }
}
</style>
